import { type ReactNode } from 'react';
import { DitheringShaderFill } from '../dithering-shader/dithering-shader-fill.js';
import { cn } from '../../../lib/cn.js';

// Same typewriter reveal + cursor blink as the editor splash screen, but
// scoped under `auth-swirl-` so the two stylesheets never collide, and the
// cursor blinks INFINITELY (the splash blinks it once before fading out).
const swirlLogoStyles = `
  @keyframes authSwirlTypeAgent {
    from { width: 0; }
    to   { width: 5ch; }
  }
  .auth-swirl-type-agent {
    display: inline-block;
    overflow: hidden;
    white-space: nowrap;
    vertical-align: bottom;
    width: 5ch;
    animation: authSwirlTypeAgent 600ms steps(5, end) forwards;
  }
  @keyframes authSwirlBrushIn {
    from { opacity: 0; transform: translateX(24px); }
    to   { opacity: 1; transform: translateX(0); }
  }
  .auth-swirl-brush {
    display: inline-block;
    font-weight: 300;
    opacity: 0;
    animation: authSwirlBrushIn 200ms cubic-bezier(0.34, 1.56, 0.64, 1) 600ms forwards;
  }
  @keyframes authSwirlBlink {
    0%, 49%   { opacity: 1; }
    50%, 100% { opacity: 0; }
  }
  .auth-swirl-cursor {
    display: inline-block;
    width: 0.55em;
    height: 1em;
    background-color: var(--color-accent-cyan);
    vertical-align: bottom;
    margin-left: 0.15em;
    animation: authSwirlBlink 1s steps(2) infinite;
  }
  @media (prefers-reduced-motion: reduce) {
    .auth-swirl-type-agent { animation: none; width: auto; }
    .auth-swirl-brush      { animation: none; opacity: 1; transform: none; }
  }
`;

interface Props {
  /** Eyebrow copy above the greeting on the left side */
  eyebrow?: ReactNode;
  /** Large heading at the top of the left panel */
  title: ReactNode;
  /** Slot for the actual form (Clerk component or custom form) */
  children: ReactNode;
  /** Optional bottom-left hint row (e.g. "New to our platform? Create an account") */
  footer?: ReactNode;
  /** Accessible label for the main landmark — i18n-driven */
  ariaLabel: string;
  className?: string;
  'data-testid'?: string;
}

/**
 * Split-screen auth shell — form column on the left, full-bleed DitheringShader
 * on the right. 50/50 split at `md+` via `md:w-1/2` on both halves so the
 * shader panel actually fills the right half of the viewport (the old `flex-1`
 * version was losing pixels to the `inset-4` framing).
 */
export function AuthShell({
  eyebrow,
  title,
  children,
  footer,
  ariaLabel,
  className,
  ...rest
}: Props) {
  return (
    <main
      aria-label={ariaLabel}
      className={cn(
        'relative flex h-[100dvh] w-full flex-col overflow-hidden bg-canvas text-primary md:flex-row',
        className,
      )}
      {...rest}
    >
      {/* Left — form column (50% at md+). Internal `overflow-y-auto` means the
          form scrolls inside the column if viewport height is very short,
          while the page itself never scrolls. */}
      <section className="relative flex h-full items-center justify-center overflow-y-auto px-6 py-6 standard:px-10 md:w-1/2">
        <div className="relative flex w-full max-w-md flex-col gap-4">
          {eyebrow && (
            <span className="agb-auth-rise agb-auth-d-100 font-mono text-caption uppercase tracking-[0.2em] text-muted">
              {eyebrow}
            </span>
          )}
          <h1 className="agb-auth-rise agb-auth-d-200 text-[clamp(1.75rem,4vw,2.5rem)] font-semibold leading-[1.05] tracking-[-0.02em] text-primary">
            {title}
          </h1>
          <div className="agb-auth-rise agb-auth-d-500">{children}</div>
          {footer && (
            <div className="agb-auth-rise agb-auth-d-700 text-center text-small text-secondary">
              {footer}
            </div>
          )}
        </div>
      </section>

      {/* Right — full-bleed dither shader (50% at md+, hidden on mobile) */}
      <section
        aria-hidden="true"
        className="agb-auth-slide agb-auth-d-300 relative hidden h-full overflow-hidden rounded-l-[4rem] border-l border-white/[0.06] md:block md:w-1/2"
      >
        <DitheringShaderFill
          shape="swirl"
          type="4x4"
          colorBack="#0a0a0a"
          colorFront="#2de1fc"
          pxSize={3}
          speed={0.5}
        />
        {/* Soft vignette so the centre reads slightly darker */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              'radial-gradient(ellipse 80% 70% at 50% 45%, rgba(10,10,10,0.35), rgba(10,10,10,0.15) 55%, transparent 85%)',
          }}
        />
        {/* AgentBrush wordmark at the centre of the swirl, with the same
            terminal typewriter + blinking cursor used by the editor splash. */}
        <style>{swirlLogoStyles}</style>
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 flex items-center justify-center"
        >
          <div
            className="flex items-center font-mono text-display tracking-[0.02em] text-primary"
            style={{ fontSize: 'clamp(2rem, 4vw, 3rem)' }}
          >
            <span className="auth-swirl-type-agent font-semibold">Agent</span>
            <span className="auth-swirl-brush text-secondary">Brush</span>
            <span className="auth-swirl-cursor" />
          </div>
        </div>
      </section>
    </main>
  );
}
